import { OrgShippingCarrier } from 'domains/orgDomain';
import { Col, Drawer, Form, Input, Radio, Row, Switch } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  CarrierTypeEnum,
  ShippingMethodNonstandardTypeEnum,
} from 'domains/shippingDomain';
import shippingService from 'services/shippingService';
import { useOrgId } from 'hooks/useOrg';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxMoney } from '../../../../components/RevxMoney';
import { UnderlineText } from '../../../../components/RevxTypography';
import { useEffect, useState } from 'react';
import { RevxButton } from '../../../../components/RevxButton';
import { RevxInputNumber } from '../../../../components/RevxInputNumber';
import { useRequest } from 'ahooks';
import { RevxSpin } from '../../../../components/RevxSpin';
import { findIndex } from 'lodash-es';

const { Will_Call, Internal_Distribution, Special_Delivery, Customize } =
  ShippingMethodNonstandardTypeEnum;

const {
  createOrgShippingConfigCarrier,
  updateOrgShippingConfigCarrier,
  deleteOrgShippingConfigCarrier,
} = shippingService;

enum ReadyDateEnum {
  sameDay = 0,
  turnaroundTime = 'Turnaround time',
}

const defaultFormValues = {
  optionName: '',
  hasRouteDistance: false,
  cost: 0,
  markupPct: 0,
  daysInTransit: ReadyDateEnum.sameDay,
};

const NoCarrierShippingOption = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [curItem, setCurItem] = useState<OrgShippingCarrier | null>();

  const orgId = useOrgId();
  const dispatch = useAppDispatch();
  const [optionForm] = Form.useForm();
  const { orgShippingConfig } = useAppSelector((state) => state.shipping);
  const { runAsync: createCarrierRun, loading: createLoading } = useRequest(
    createOrgShippingConfigCarrier,
    {
      manual: true,
    }
  );
  const { runAsync: updateCarrierRun, loading: updateLoading } = useRequest(
    updateOrgShippingConfigCarrier,
    {
      manual: true,
    }
  );
  const { runAsync: deleteCarrierRun, loading: deleteLoading } = useRequest(
    deleteOrgShippingConfigCarrier,
    {
      manual: true,
    }
  );

  const getLoading = useAppSelector(
    (state) => state.loading.effects.shipping.getOrgShippingConfig
  );

  useEffect(() => {
    if (drawerVisible) {
      optionForm.setFieldsValue(
        curItem ? convertBackendDataToFormStructure(curItem) : defaultFormValues
      );
    }
  }, [drawerVisible, curItem, optionForm]);

  function convertBackendDataToFormStructure(
    nonstandard?: OrgShippingCarrier | null
  ) {
    if (!nonstandard?.nonstandard) return defaultFormValues;
    const {
      optionName,
      hasRouteDistance,
      routeDistance,
      cost,
      daysInTransit,
      markupPct,
    } = nonstandard.nonstandard;

    const { fromMile, toMile } = routeDistance || {};
    const isSameDay = daysInTransit === ReadyDateEnum.sameDay;
    const daysInTransitValue = isSameDay
      ? ReadyDateEnum.sameDay
      : ReadyDateEnum.turnaroundTime;
    return {
      hasRouteDistance,
      daysInTransit: daysInTransitValue,
      fromMile,
      toMile,
      optionName,
      cost,
      markupPct,
      turnaroundTime: !isSameDay ? daysInTransit : undefined,
    };
  }
  const nonstandardShippingMethods = orgShippingConfig?.carriers
    .filter((item) => item.carrierType === CarrierTypeEnum.Nonstandard)
    .sort((a, b) => a.serialNumber - b.serialNumber);

  const allowHandler = async (item: OrgShippingCarrier) => {
    const res = await shippingService.updateOrgShippingConfigCarrier(
      orgId,
      item
    );
    if (orgShippingConfig) {
      const carriers = [...orgShippingConfig.carriers];
      const { serialNumber } = res;
      const index = findIndex(carriers, { serialNumber });
      if (index > -1) {
        carriers[index] = res;
      }
      dispatch.shipping.setState({
        orgShippingConfig: {
          ...orgShippingConfig,
          carriers,
        },
      });
    }
  };

  const NoCarrierShippingOptionItem = ({
    item,
    allowHandler,
  }: {
    item: OrgShippingCarrier;
    allowHandler: (item: OrgShippingCarrier) => void;
  }) => {
    if (!item.nonstandard) return null;
    const { nonstandard, allowed } = item;
    const {
      nonstandardType,
      hasRouteDistance,
      cost,
      daysInTransit,
      optionName,
      routeDistance,
      markupPct,
    } = nonstandard;
    const showActions = !(
      nonstandardType === Will_Call || nonstandardType === Special_Delivery
    );
    return (
      <RevxFlexLayout
        className="no-carrier__item"
        hidden={nonstandardType === Internal_Distribution}
      >
        <Col flex={1}>{optionName}</Col>
        <Col flex={1}>
          {hasRouteDistance
            ? `${routeDistance?.fromMile}mi - ${routeDistance?.toMile}mi`
            : 'No'}
        </Col>
        <Col flex={1}>
          <RevxMoney value={cost} />
        </Col>
        <Col flex={1}>{`${markupPct}%`}</Col>
        <Col flex={1}>{daysInTransit}</Col>
        <Col flex={1}>
          <Switch
            defaultChecked={allowed}
            onChange={(checked) => {
              allowHandler({ ...item, allowed: checked });
            }}
          />
        </Col>
        <Col flex={1}>
          {showActions && (
            <div>
              <span className="mr-10">
                <UnderlineText
                  color="blue"
                  onClick={() => {
                    setCurItem(item);
                    setDrawerVisible(true);
                  }}
                >
                  Update
                </UnderlineText>
              </span>
              <UnderlineText
                color="blue"
                onClick={async () => {
                  await deleteCarrierRun(orgId, item.serialNumber);
                  await dispatch.shipping.getOrgShippingConfig(orgId);
                }}
              >
                Remove
              </UnderlineText>
            </div>
          )}
        </Col>
      </RevxFlexLayout>
    );
  };

  const saveShippingOption = async (values: any) => {
    const {
      hasRouteDistance,
      daysInTransit,
      fromMile,
      toMile,
      optionName,
      cost,
      markupPct,
      turnaroundTime,
    } = values;
    const routeDistance = hasRouteDistance ? { fromMile, toMile } : undefined;
    const daysInTransitValue =
      daysInTransit === ReadyDateEnum.sameDay ? 0 : turnaroundTime;
    const payload = {
      carrierType: CarrierTypeEnum.Nonstandard,
      allowed: curItem?.allowed || false,
      nonstandard: {
        nonstandardType: Customize,
        optionName,
        hasRouteDistance,
        routeDistance,
        cost,
        daysInTransit: daysInTransitValue,
        markupPct,
      },
      serialNumber: curItem?.serialNumber,
    };
    if (curItem) {
      await updateCarrierRun(orgId, payload);
    } else {
      await createCarrierRun(orgId, payload);
    }
    await dispatch.shipping.getOrgShippingConfig(orgId);
    setDrawerVisible(false);
    setCurItem(undefined);
  };

  return (
    <RevxSpin spinning={getLoading || deleteLoading}>
      <div className="no-carrier" id="no-carrier-layout">
        <div className="no-carrier__title">Setup Shipping Options</div>
        <div className="no-carrier__header border-bottom-none">
          <Row>
            <Col>Shipping Option</Col>
            <Col>
              <div>Include a delivery</div> <div>route range</div>
            </Col>
            <Col>Cost</Col>
            <Col>Markup</Col>
            <Col>
              Shipping Time from <div>Ready Date</div>
            </Col>
            <Col>Active or Inactive</Col>
            <Col>Action</Col>
          </Row>
        </div>
        <div className="no-carrier__content">
          <Row wrap={true}>
            {nonstandardShippingMethods?.map((item, index) => (
              <NoCarrierShippingOptionItem
                item={item}
                allowHandler={allowHandler}
                key={index}
              />
            ))}
          </Row>
        </div>
        <div className="no-carrier__footer mt-20 mb-40">
          <UnderlineText
            color="blue"
            onClick={() => {
              setDrawerVisible(true);
            }}
          >
            + Add shipping option
          </UnderlineText>
        </div>
        <Drawer
          className="no-carrier-layout__drawer"
          destroyOnClose
          title={curItem ? 'Edit Shipping Option' : 'Create Shipping Option'}
          visible={drawerVisible}
          closable={true}
          width={900}
          onClose={() => {
            setDrawerVisible(false);
            setCurItem(null);
          }}
        >
          <div className="shipping-option-form">
            <Form form={optionForm} onFinish={saveShippingOption}>
              <Form.Item
                label="Option name"
                name="optionName"
                rules={[{ required: true }]}
              >
                <Input maxLength={40} placeholder="Enter a shipping name" />
              </Form.Item>
              <Form.Item
                label="Include a route distance"
                name="hasRouteDistance"
                rules={[{ required: true }]}
              >
                <Radio.Group>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                noStyle={true}
                shouldUpdate={true}
                dependencies={['hasRouteDistance']}
              >
                {(form) => {
                  return (
                    <>
                      {form.getFieldValue('hasRouteDistance') && (
                        <RevxFlexLayout
                          align="start"
                          className="distance-range"
                        >
                          <Form.Item
                            className="from-mile"
                            name="fromMile"
                            label="Delivery route distance range"
                            dependencies={['toMile']}
                            rules={[
                              { required: true },
                              {
                                validator: (rule, value) => {
                                  const v = optionForm.getFieldValue('toMile');
                                  if (value < v) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(
                                    'min value must be less than max'
                                  );
                                },
                              },
                            ]}
                          >
                            <RevxInputNumber min={0} />
                          </Form.Item>
                          <span className="mile">mi. to</span>
                          <Form.Item
                            name="toMile"
                            rules={[{ required: true, message: '' }]}
                            className="to-mile"
                            dependencies={['fromMile']}
                          >
                            <RevxInputNumber min={0} />
                          </Form.Item>
                          <span className="mile">mi.</span>
                        </RevxFlexLayout>
                      )}
                    </>
                  );
                }}
              </Form.Item>
              <Form.Item label="Cost" name="cost" rules={[{ required: true }]}>
                <RevxInputNumber min={0} />
              </Form.Item>
              <Form.Item
                label="Markup"
                name="markupPct"
                rules={[{ required: true }]}
              >
                <RevxInputNumber prefix="%" />
              </Form.Item>
              <RevxFlexLayout className="ready-date" align="center">
                <Form.Item
                  label="Shipping Time from Ready Date"
                  name="daysInTransit"
                  rules={[{ required: true }]}
                >
                  <Radio.Group>
                    <Radio value={ReadyDateEnum.sameDay}>Same Day</Radio>
                    <Radio value={ReadyDateEnum.turnaroundTime}>
                      Turnaround time
                    </Radio>
                  </Radio.Group>
                </Form.Item>
                <RevxFlexLayout className="ready-date__input">
                  <Form.Item
                    dependencies={['daysInTransit']}
                    shouldUpdate={true}
                  >
                    {(form) => {
                      const isSameDay =
                        form.getFieldValue('daysInTransit') ===
                        ReadyDateEnum.sameDay;
                      return (
                        <>
                          {!isSameDay && (
                            <RevxFlexLayout>
                              <Form.Item
                                name="turnaroundTime"
                                rules={[
                                  {
                                    required: true,
                                    message: '',
                                  },
                                ]}
                              >
                                <RevxInputNumber min={1} isInteger={true} />
                              </Form.Item>
                              <span className="days">days</span>
                            </RevxFlexLayout>
                          )}
                        </>
                      );
                    }}
                  </Form.Item>
                </RevxFlexLayout>
              </RevxFlexLayout>
              <Form.Item className="actions">
                <RevxButton
                  className="mr-10"
                  type="primary"
                  brandPurple={true}
                  htmlType="submit"
                  loading={createLoading || updateLoading}
                >
                  Save
                </RevxButton>
                <RevxButton
                  onClick={() => {
                    setDrawerVisible(false);
                    setCurItem(null);
                  }}
                >
                  Cancel
                </RevxButton>
              </Form.Item>
            </Form>
          </div>
        </Drawer>
      </div>
    </RevxSpin>
  );
};

export default NoCarrierShippingOption;
